import { useMemo, useRef } from "react";
import { InfiniteData, useInfiniteQuery } from "@tanstack/react-query";
import NetworkPortfolioManagementService from "@app/network-portfolio-management/services/NetworkPortfolioManagementService";
import { NetworkPortfolioManagementProject, SortByOption } from "@app/network-portfolio-management/interfaces";
import { NPM_QUERY_KEYS } from "@app/network-portfolio-management/npmQueryKeys";

export const PAGE_SIZE = 25;

interface UseProjectInfiniteQueryOptions {
  isSorted?: boolean;
  projectIds: number[];
  sortBy: SortByOption;
}

interface UseProjectInfiniteQueryResult {
  error: Error | null;
  fetchNextPage: () => void;
  hasLoadedAny: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  /** Rows grouped by fetch page, in fetch order — lets a caller batch per-page work (e.g. a
   * secondary lookup keyed by that page's own project IDs) instead of per-row or across the
   * whole accumulated list. */
  pages: NetworkPortfolioManagementProject[][];
  rows: NetworkPortfolioManagementProject[];
}

/**
 * Two paging modes, matching the project-resources endpoints:
 * - isSorted=false (AlphabeticalAZ): passes all A→Z IDs to the backend with a page index; the
 *   backend does Skip/Take and returns the page.
 * - isSorted=true (all other sorts, grouped): the IDs are pre-sorted; each page slices the array
 *   client-side and sends only PAGE_SIZE IDs to the backend for enrichment.
 */
export const useProjectInfiniteQuery = ({
  isSorted = false,
  projectIds,
  sortBy,
}: UseProjectInfiniteQueryOptions): UseProjectInfiniteQueryResult => {
  const service = useRef(new NetworkPortfolioManagementService()).current;
  const totalCount = projectIds.length;

  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useInfiniteQuery<
    NetworkPortfolioManagementProject[],
    Error,
    InfiniteData<NetworkPortfolioManagementProject[]>,
    readonly unknown[],
    number
  >({
    enabled: totalCount > 0,
    gcTime: 10 * 60 * 1000,
    // Two different "is there more data?" signals, matching the two paging modes:
    // - isSorted: we already know the exact ID count (totalCount) and slice it into PAGE_SIZE
    //   chunks client-side, so "more data" means "more ID slices left to request" — independent of
    //   how many rows a given slice actually resolves to (some IDs in a slice may be filtered out).
    // - !isSorted: the backend does Skip/Take over an ID list we don't control the filtering of, so
    //   the definitive end-of-data signal is the backend returning fewer rows than requested.
    getNextPageParam: (lastPage, allPages) =>
      isSorted
        ? (allPages.length * PAGE_SIZE < totalCount ? allPages.length + 1 : undefined)
        : (lastPage.length < PAGE_SIZE ? undefined : allPages.length + 1),
    initialPageParam: 1,
    queryFn: ({ pageParam, signal }) => {
      const pageIds = isSorted
        ? projectIds.slice((pageParam - 1) * PAGE_SIZE, pageParam * PAGE_SIZE)
        : projectIds;
      const pageIndex = isSorted ? 1 : pageParam;
      return service.getProjectsData(
        { pageIndex, pageSize: PAGE_SIZE, projectIds: pageIds, sortBy: isSorted ? SortByOption.Unknown : sortBy },
        signal,
      );
    },
    queryKey: [NPM_QUERY_KEYS.projects, projectIds, sortBy, isSorted],
    staleTime: 5 * 60 * 1000,
  });

  const rows = useMemo<NetworkPortfolioManagementProject[]>(() => (data ? data.pages.flat() : []), [data]);

  return {
    error: error ?? null,
    fetchNextPage,
    hasLoadedAny: !!data?.pages.length,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    isLoading,
    pages: data?.pages ?? [],
    rows,
  };
};
